import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { DEBOUNCE_TIME_NORMAL } from "~/common/constants";
import { GdtfDateRangeInput } from "~/components/gdtf-date-input";

interface CompoundGdtfFilterProps {
  /** Committed values (from the URL). */
  gdtfFrom?: string;
  gdtfTo?: string;
  /** Commit a new range to the URL (debounced by this component). */
  onCommit: (from: string | undefined, to: string | undefined) => void;
}

/**
 * GDTF date-range filter with a local draft committed to the URL on a debounce —
 * mirrors the V1 list behaviour so typing doesn't fire a query per keystroke.
 */
export function CompoundGdtfFilter({ gdtfFrom, gdtfTo, onCommit }: CompoundGdtfFilterProps) {
  const { t } = useTranslation();
  const [from, setFrom] = useState(gdtfFrom ?? "");
  const [to, setTo] = useState(gdtfTo ?? "");

  // Re-seed drafts when the committed URL values change from outside (back/forward, shared link).
  const prevRef = useRef({ gdtfFrom, gdtfTo });
  if (prevRef.current.gdtfFrom !== gdtfFrom || prevRef.current.gdtfTo !== gdtfTo) {
    prevRef.current = { gdtfFrom, gdtfTo };
    setFrom(gdtfFrom ?? "");
    setTo(gdtfTo ?? "");
  }

  const commit = useDebouncedCallback(
    (nextFrom: string, nextTo: string) => onCommit(nextFrom.trim() || undefined, nextTo.trim() || undefined),
    DEBOUNCE_TIME_NORMAL,
  );

  return (
    <div className="max-w-[28rem]">
      <GdtfDateRangeInput
        label={t("feature.entities.search.dateGdtf")}
        from={from}
        to={to}
        onFromChange={(v) => {
          setFrom(v);
          commit(v, to);
        }}
        onToChange={(v) => {
          setTo(v);
          commit(from, v);
        }}
      />
    </div>
  );
}
